import { useDepot } from '#~/depot/hooks.js';
import { useLayoutEffect, type ReactElement } from 'react';
import { Outlet } from 'react-router-dom';

/**
 * `<BootReadyLayout>` — pathless layout route that publishes
 * `boot.isRootReady = true` for non-room deep-link routes under the
 * boot-protected subtree.
 *
 * Why (ENG-4186): `<BootOverlay>` (router.tsx) keeps the "Loading your studio…"
 * screen up while `visible = … || !isRootReady`. The codebase convention is that
 * deep-link routes that are final on mount publish `isRootReady = true` before
 * paint. The add-device wizard routes the embedded-device flow deep-links into
 * (`/agent/setup/*`, `/agent/link/*`, `/agent/linking`, agent lookup) never did
 * — so a direct deep-link, where `<Root>` never mounts to set the flag, hung the
 * overlay forever. Centralizing the publish here means non-room deep-link routes
 * under this layout inherit dismissal and no future route in that group can
 * silently regress. Normal room routes stay outside this layout because room
 * routes own their visible-ready signal after room data/agent gates resolve.
 * The single-route precedent was agent/setup/setup.tsx (commit 8fc4c1dca); this
 * consolidates it.
 *
 * `useLayoutEffect` (NOT `useEffect`): runs synchronously after DOM commit but
 * BEFORE paint, so `isRootReady` flips and the overlay re-renders to opacity-0
 * within the same frame the route first mounts — no one-frame "Loading your
 * studio…" flash.
 *
 * `<Root>` and normal room routes stay OUTSIDE this layout: both legitimately
 * gate the overlay on route-owned readiness. The overlay's other dimensions
 * (autoLink / hasRegistryLoaded / bootError) still gate every route here, so
 * this only removes the `isRootReady` block. On a cold deep-link the
 * `<AutoLinkGuard>` (which only wraps the index route) never mounts, so
 * `boot.autoLinkState` stays at its `idle` default — which the overlay treats as
 * non-blocking (`computeReason` / `autoLinkInProgress` in boot-overlay.tsx).
 * That `idle`-is-non-blocking invariant is what lets the overlay dismiss here;
 * if it ever changes, deep-links would need an explicit `skipped` publish too.
 *
 * Per ai.knowledge/global rules `code-comments-required`,
 * `reference-rules-in-comments`.
 */
export const BootReadyLayout = (): ReactElement => {
  const produce = useDepot((µ) => µ.produce);

  useLayoutEffect(() => {
    produce((draft) => {
      draft.boot.isRootReady = true;
    });
  }, [produce]);

  return <Outlet />;
};
